import { differenceInDays } from "date-fns";
import { ChevronRight } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { formatRelativeDate } from "../misc/RelativeDate";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Sale } from "../types";
import { AccountTasks } from "./AccountTasks";

/** Cells rendered below, so the expanded panel spans the whole row. */
const ACCOUNT_CELL_COUNT = 7;
/** Days without any activity before the last touch reads as a warning. */
const STALE_DAYS = 14;
/** Days without any activity before the last touch reads as critical. */
const CRITICAL_DAYS = 30;

/**
 * One account in the accounts table, with a chevron expanding its open tasks
 * in place. Only the company name navigates.
 */
export const AccountsTableRow = ({ company }: { company: Company }) => {
  const translate = useTranslate();
  const [locale = "en"] = useLocaleState();
  const { currency } = useConfigurationContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const overdueCount = company.nb_overdue_tasks ?? 0;

  return (
    <>
      <TableRow>
        <TableCell className="w-8">
          <Button
            variant="ghost"
            size="icon"
            aria-expanded={isExpanded}
            aria-label={translate(
              isExpanded
                ? "crm.dashboard.accounts.collapse"
                : "crm.dashboard.accounts.expand",
            )}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            className="size-7 cursor-pointer focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight
              className={cn("size-4 transition-transform", {
                "rotate-90": isExpanded,
              })}
            />
          </Button>
        </TableCell>

        <TableCell>
          <div className="flex items-center gap-2">
            <CompanyAvatar record={company} width={20} height={20} />
            <div className="flex flex-col">
              <Link
                to={`/companies/${company.id}/show`}
                className="font-medium hover:underline"
              >
                {company.name}
              </Link>
              {company.sector && (
                <span className="text-xs text-muted-foreground">
                  {company.sector}
                </span>
              )}
            </div>
          </div>
        </TableCell>

        <TableCell>
          <AccountOwner company={company} />
        </TableCell>

        <TableCell>
          <span className="tabular-nums">{company.nb_open_tasks ?? 0}</span>
          {overdueCount > 0 && (
            /*
              A middot, not just a margin: "15" beside "7 late" reads as one
              number at a glance, and the two counts mean different things.
            */
            <span
              className="mx-1 text-xs text-muted-foreground"
              aria-hidden="true"
            >
              ·
            </span>
          )}
          {overdueCount > 0 && (
            <span className="text-xs text-destructive tabular-nums">
              {translate("crm.dashboard.accounts.overdue_suffix", {
                count: overdueCount,
              })}
            </span>
          )}
        </TableCell>

        <TableCell>
          {company.next_task_due ? (
            <span>{formatRelativeDate(company.next_task_due, locale)}</span>
          ) : (
            <span className="text-muted-foreground">
              {translate("crm.dashboard.accounts.none")}
            </span>
          )}
        </TableCell>

        <TableCell className="text-right tabular-nums">
          {formatCompactCurrency(
            company.open_deals_amount ?? 0,
            currency,
            locale,
          )}
        </TableCell>

        <TableCell>
          {company.last_activity_at ? (
            <span className={getLastTouchToneClass(company.last_activity_at)}>
              {formatRelativeDate(company.last_activity_at, locale)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow>
          <TableCell colSpan={ACCOUNT_CELL_COUNT} className="bg-muted/30 p-0">
            <AccountTasks company={company} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AccountOwner = ({ company }: { company: Company }) => {
  const translate = useTranslate();

  // Say so explicitly when nobody owns the account: rendering nothing would
  // read as an ordinary row rather than the one most likely to need an owner.
  if (company.sales_id == null) {
    return (
      <span className="text-amber-600 dark:text-amber-500">
        {translate("crm.dashboard.accounts.unowned")}
      </span>
    );
  }

  return (
    <ReferenceField<Company, Sale>
      source="sales_id"
      reference="sales"
      record={company}
      link={false}
      render={({ referenceRecord }) => {
        if (!referenceRecord) return null;
        const fullName = `${referenceRecord.first_name} ${referenceRecord.last_name}`;
        return (
          <div className="flex items-center gap-2">
            <Avatar className="w-[20px] h-[20px]">
              <AvatarImage src={referenceRecord.avatar?.src} alt={fullName} />
              <AvatarFallback className="text-[10px]">
                {referenceRecord.first_name?.charAt(0).toUpperCase()}
                {referenceRecord.last_name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm">{fullName}</span>
          </div>
        );
      }}
    />
  );
};

/**
 * Amounts are compared column-wise here rather than read precisely, so they are
 * abbreviated ("$1.2M") and kept in the configured currency.
 */
const formatCompactCurrency = (
  amount: number,
  currency: string,
  locale: string,
) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);

const getLastTouchToneClass = (lastActivityAt: string) => {
  const days = differenceInDays(new Date(), new Date(lastActivityAt));
  if (days > CRITICAL_DAYS) return "text-destructive";
  if (days >= STALE_DAYS) return "text-amber-600 dark:text-amber-500";
  return "text-muted-foreground";
};
